import React, { useEffect, useState } from 'react';
import {
    Dropdown,
    DropdownToggle,
    DropdownMenu,
    Badge,
    Button,
    UncontrolledTooltip,
} from 'reactstrap';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import listPlugin from "@fullcalendar/list";
import BootstrapTheme from "@fullcalendar/bootstrap";
import interactionPlugin from '@fullcalendar/interaction';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import { meetingsListGetRequest, meetingssMetadataGetRequest, meetingsUpdateRequest } from 'store/meetings/action';
import { getCalendarEvents, getGoogleCalendarData } from 'store/calendarSync/action';
import { isJson } from 'utils';
import EventDetailsModal from './RecordDetails/Modals/EventDetailsModal';


const BYDAY: any = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };

const CalendarDropdown = () => {
    const dispatch: any = useDispatch();
    const userProfile = useSelector((state: ApplicationState) => state.auth.userProfile);
    const eventDataModel: any = useSelector((state: ApplicationState) => state.meetings.assigneeMeetings);
    const googleConnect = useSelector((state: ApplicationState) => state.calendarSync.data);
    const eventData = useSelector((state: ApplicationState) => state.calendarSync?.events);
    const meetingMetaData: any = useSelector((state: ApplicationState) => state.meetings.metaDataFields);
    const [open, setOpen] = useState(false);
    const [statusOptions, setStatusOptions] = useState<{ label: string; value: string }[]>([]);
    const [eventsArr, setEvents] = useState<any>([]);
    const [isEdit, setIsEdit] = useState<boolean>(false);
    const [eventsLength, setEventsLegth] = useState<boolean>(false)
    const [detailsModal, setDetailsModal] = useState<any>(false);
    const [disabled, setDisabled] = useState(false);
    const [modal, setModal] = useState<boolean>(false);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [event, setEvent] = useState<any>({});
    const [filterData, setFilterData] = useState<any>()
    useEffect(() => {
        if (googleConnect?.length) {
            const email = googleConnect[0]?.email;
            const provider = googleConnect[0]?.provider;
            dispatch(getCalendarEvents(email, provider));
        }
    }, [googleConnect]);

    useEffect(() => {
        const filterObject = { pageSize: 500 };
        const data = {
            filters: [
                {
                    quick: [],
                    advance: [],
                    search: null,
                    parent: [
                        {
                            key: "assignee",
                            keyLabel: ["Assignee"],
                            condition: "IN",
                            conditionLabel: "is any of",
                            values: [userProfile.id],
                            valuesLabel: [],
                            property: {},
                            quick: true
                        }
                    ]
                }
            ],
            sorts: [{ field: "dueDate", order: "desc" }]
        };
        setFilterData(data)
        dispatch(meetingsListGetRequest(data, 0, undefined));
        dispatch(meetingssMetadataGetRequest(filterObject));
        dispatch(getGoogleCalendarData());
    }, []);

    useEffect(() => {
        if (!meetingMetaData?.length) return;
        let extractedStatus: { label: string; value: string }[] = [];
        meetingMetaData.forEach((field: any) => {
            if (field?.valuesJson && isJson(field.valuesJson)) {
                const parsedJson = JSON.parse(field.valuesJson);
                if (Array.isArray(parsedJson.values)) {
                    extractedStatus = parsedJson.values.map((item: any) => ({
                        label: item.label,
                        value: item.value
                    }));
                }
            }
        });
        setStatusOptions(extractedStatus);
    }, [meetingMetaData]);

    const getGoogleDateValue = (timeObj: any) => {
        if (!timeObj) return null;
        // Sometimes Google returns an object with a dateTime property that contains a "value".
        if (timeObj.dateTime) return timeObj.dateTime.value || timeObj.dateTime;
        return timeObj;
    };

    const getMicrosoftDateValue = (timeObj: any) => {
        if (!timeObj) return null;
        return timeObj.dateTime;
    };

    useEffect(() => {
        const mergedEvents: any = [];

        // Process backend meetings
        eventDataModel?.forEach((meeting: { id: any; valuesJson: any; }) => {
            const { id, valuesJson } = meeting;
            const {
                assignee,
                status,
                start_time,
                end_time,
                location,
                event_name,
                description,
                meeting_type,
                invitee_email,
                location_address,
                link
            } = valuesJson || {};
            if (!status || !start_time) {
                console.warn(`Meeting with ID ${id} is missing status or start time. Skipping.`);
                return;
            }
            const startDate = new Date(start_time);
            if (isNaN(startDate.getTime())) {
                console.warn(`Backend meeting with ID ${id} has invalid start time. Skipping.`);
                return;
            }
            const endDate = end_time ? new Date(end_time) : null;
            mergedEvents.push({
                id,
                title: event_name,
                description,
                locationType: location,
                address: location_address,
                meetingLink: link,
                start: startDate.toISOString(),
                end: endDate && !isNaN(endDate.getTime()) ? endDate.toISOString() : null,
                className: "bg-info-subtle",
                meetingType: meeting_type,
                invitee: invitee_email?.email || "N/A",
                status: status || "",
                attendees: assignee
                    ? assignee?.length ? assignee?.map((item: any) => {
                        return {
                            email: item?.email || item?.name || "Unknown",
                            responseStatus: null
                        }
                    }) : [{
                        email: assignee?.email || assignee?.name || "Unknown",
                        responseStatus: null
                    }]
                    : []
            });
        });

        // Determine which provider is connected.
        const provider = googleConnect?.[0]?.provider || "google";
        eventData?.forEach((ev: any) => {
            if (provider === "microsoft") {
                // Process all events as Microsoft events.
                const id = ev.id;
                const title = ev.subject;
                const startTime = getMicrosoftDateValue(ev.start);
                const endTime = getMicrosoftDateValue(ev.end);
                const meetingLink = ev.onlineMeeting ? ev.onlineMeeting.joinUrl : "";
                const locationType = ev.onlineMeeting ? "online" : "offline";
                const address = ev.location ? ev.location.displayName : "";
                const description = ev.body?.content || ev.description || "";
                if (!startTime) {
                    console.warn(`Microsoft event with ID ${id} missing start time. Skipping.`);
                    return;
                }
                const startDate = new Date(startTime);
                if (isNaN(startDate.getTime())) {
                    console.warn(`Microsoft event with ID ${id} has invalid start time. Skipping.`);
                    return;
                }
                const endDate = endTime ? new Date(endTime) : null;
                mergedEvents.push({
                    id,
                    title,
                    description,
                    locationType,
                    address,
                    meetingLink,
                    start: startDate.toISOString(),
                    end: endDate && !isNaN(endDate.getTime()) ? endDate.toISOString() : null,
                    className: "bg-info-subtle",
                    status: ev.status || ""
                });
            } else {
                // Process all events as Google events.
                // Check for recurring events.
                const { id, summary, start, end, hangoutLink, location, description, status, location_address, link, recurrence, attendees = [] } = ev;

                const formattedAttendees = attendees.map((att: any) => ({
                    email: att.email,
                    responseStatus: att.responseStatus
                }));

                if (recurrence && recurrence.length > 0) {
                    const rule = recurrence[0].split(";").reduce((acc: any, item: string) => {
                        const [key, value] = item.split("=");
                        acc[key] = value;
                        return acc;
                    }, {});
                    const byday = rule["BYDAY"] ? rule["BYDAY"].split(",").map((d: string) => BYDAY[d]) : [];
                    const startTimeZoneShift = start?.dateTime?.timeZoneShift || 0;
                    const endTimeZoneShift = end?.dateTime?.timeZoneShift || 0;
                    const startValue = getGoogleDateValue(start);
                    const endValue = getGoogleDateValue(end);
                    if (!startValue) {
                        console.warn(`Recurring Google event with ID ${id} missing start time. Skipping.`);
                        return;
                    }
                    const startDate = new Date(startValue);
                    if (isNaN(startDate.getTime())) {
                        console.warn(`Recurring Google event with ID ${id} has invalid start time. Skipping.`);
                        return;
                    }
                    mergedEvents.push({
                        id,
                        title: summary,
                        description,
                        daysOfWeek: byday,
                        startTime: startValue
                            ? new Date(startValue + startTimeZoneShift * 60 * 1000)
                                .toISOString()
                                .split("T")[1]
                                .split(".")[0]
                            : "",
                        endTime: endValue && endValue
                            ? new Date(endValue + endTimeZoneShift * 60 * 1000)
                                .toISOString()
                                .split("T")[1]
                                .split(".")[0]
                            : "",
                        endRecur: rule.UNTIL,
                        className: "bg-info-subtle",
                        meetingLink: hangoutLink ? hangoutLink : link,
                        status: status || "",
                        attendees: formattedAttendees
                    });
                } else {
                    const startValue = getGoogleDateValue(start);
                    const endValue = getGoogleDateValue(end);
                    if (!startValue) {
                        console.warn(`Google event with ID ${id} missing start time. Skipping.`);
                        return;
                    }
                    const startDate = new Date(startValue);
                    if (isNaN(startDate.getTime())) {
                        console.warn(`Google event with ID ${id} has invalid start time. Skipping.`);
                        return;
                    }
                    const endDate = endValue ? new Date(endValue) : null;
                    mergedEvents.push({
                        id,
                        title: summary,
                        description,
                        locationType: location,
                        address: location_address,
                        meetingLink: hangoutLink ? hangoutLink : link,
                        start: startDate.toISOString(),
                        end: endDate && !isNaN(endDate.getTime()) ? endDate.toISOString() : null,
                        className: "bg-info-subtle",
                        status: status || "",
                        attendees: formattedAttendees
                    });
                }
            }
        });

        setEvents(mergedEvents);
        setEventsLegth(true)
    }, [dispatch, eventData, eventDataModel, googleConnect]);
    console.log("userProfile", userProfile)
    const handleDateSelect = (selectInfo: any) => {
        const calendarApi = selectInfo.view.calendar;
        const start = selectInfo.startStr;
        const end = selectInfo.endStr;
        let title;
        if (title) {
            calendarApi.addEvent({
                title,
                start,
                end,
                allDay: false
            });
        }
    };
    const handleEventClick = (arg: any) => {
        const clickedEvent = arg.event;
        const startDate = clickedEvent.start;
        const endDate =
            clickedEvent.end || new Date(new Date(startDate).getTime() + 60 * 60 * 1000);
        const formattedDate = moment(startDate).format("DD MMM YYYY");
        const formattedStartTime = moment(startDate).format("hh:mm A");
        const formattedEndTime = moment(endDate).format("hh:mm A");

        const rawDescription = clickedEvent.extendedProps?.description || "";

        setEvent({
            id: clickedEvent.id,
            title: clickedEvent.title,
            start: startDate,
            end: endDate,
            locationType: clickedEvent.extendedProps?.locationType || "N/A",
            address: clickedEvent.extendedProps?.address || "",
            meetingLink: clickedEvent.extendedProps?.meetingLink || "",
            description: rawDescription,
            formattedDate,
            formattedTime: `${formattedStartTime} - ${formattedEndTime}`,
            status: clickedEvent.extendedProps?.status || "Unknown",
            attendees: clickedEvent.extendedProps?.attendees || [],
        });

        setIsEdit(true);
        setDetailsModal(true);
    };

    const formatResponseStatus = (status: string) => {
        switch (status.toLowerCase()) {
            case "needsaction":
                return "Needs Action";
            case "accepted":
                return "Accepted";
            case "declined":
                return "Declined";
            case "tentative":
                return "Tentative";
            default:
                return status;
        }
    };

    const getStatusColor = (status: string) => {
        switch (status.toLowerCase()) {
            case "accepted":
                return "green";
            case "needsaction":
                return "orange";
            case "declined":
                return "red";
            case "tentative":
                return "blue";
            default:
                return "black";
        }
    };
    const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

    const handleStatusUpdate = (eventId: any, newStatus: { label: string; value: string }) => {
        setDisabled(true);
        setEvent((prev: any) => ({ ...prev, status: newStatus.value }));
        const updatedData = { status: newStatus.value };
        const handleSuccess = () => {
            dispatch(meetingsListGetRequest(filterData, 0, undefined));
            setDetailsModal(false)
            setDisabled(false);
            setDropdownOpen(false);
            setModal(false);
        };
        const handleFailure = () => {
            setDisabled(false);
        };
        dispatch(meetingsUpdateRequest(eventId, updatedData, handleSuccess, handleFailure));
    };
    return (
        <>
            <EventDetailsModal
                isOpen={detailsModal}
                toggle={() => {
                    setDetailsModal(false);
                    setEvent(null);
                }}
                event={event}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                statusOptions={statusOptions}
                handleStatusUpdate={handleStatusUpdate}
                getStatusColor={getStatusColor}
                formatResponseStatus={formatResponseStatus}
            />
            <Dropdown isOpen={open} toggle={() => setOpen(!open)}>
                <DropdownToggle
                    tag="button"
                    className="btn btn-icon btn-topbar btn-ghost-secondary rounded-circle position-relative"
                >
                    <i id="calendarDropdown" className="bx bx-calendar fs-22" />
                    <Badge
                        color="info"
                        pill
                        className="position-absolute top-0 end-0 ms-2"
                        style={{ fontSize: '0.6rem' }}
                    >
                        {eventsLength ? eventsArr.length : null}
                    </Badge>
                </DropdownToggle>
                <UncontrolledTooltip placement="top" target="calendarDropdown">
                    Calendar
                </UncontrolledTooltip>
                <DropdownMenu
                    end
                    style={{
                        width: 580,
                        padding: '0.75rem',
                        borderRadius: 12,
                        overflow: 'hidden',
                        zIndex: 1050,
                    }}
                >
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <FullCalendar
                            plugins={[BootstrapTheme, dayGridPlugin, interactionPlugin, listPlugin]}
                            select={handleDateSelect}
                            initialView="dayGridMonth"
                            slotDuration={"00:15:00"}
                            themeSystem="bootstrap"
                            headerToolbar={{
                                left: "prev,next today",
                                center: "title",
                                right: "dayGridMonth,dayGridWeek,dayGridDay,listWeek"
                            }}
                            events={eventsArr}
                            editable={true}
                            droppable={true}
                            selectable={true}
                            //  dateClick={handleDateClick}
                            eventClick={handleEventClick}
                        />
                    </div>
                </DropdownMenu>
            </Dropdown>
        </>

    );
}

export default CalendarDropdown;